import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import { buildSignupUrl } from '../../lib/signupUrl';

const PricingSection = () => {
  const signupUrl = buildSignupUrl();

  const plans = [
    {
      name: 'Basic',
      price: '$15',
      period: '/month',
      description: 'For growing businesses needing more integrations.',
      features: [
        '10 Projects',
        '15 Video exports',
        '100 AI images',
        '500MB Storage',
        '1 Team member',
        '10 Voice regenerations per project',
        '3min Video script limit',
        'Upto 1 hr Podcast',
        'Pro voices',
        '720p Video resolution',
        '100M+ media assets library',
        'Live video editor',
        'Chat Support',
      ],
      cta: 'Start free trial',
      ctaLink: signupUrl,
      highlighted: false,
    },
    {
      name: 'Premium',
      price: '$45',
      period: '/month',
      description: 'For creators making premium content for global audiences.',
      features: [
        '20 Projects',
        '30 Video exports',
        '200 AI images',
        'Nano Banana PRO',
        '10 AI Video (Veo3, Runway, Grok, Haliuo)',
        '1GB Storage',
        '5 Team members',
        '5min Video script limit',
        'Upto 2 hr Podcast',
        '1080p Video resolution',
        'Publish to Youtube',
        'Brand kit',
        'API access',
      ],
      cta: 'Start free trial',
      ctaLink: signupUrl,
      highlighted: true,
    },
  ];

  return (
    <section id="pricing" className="py-24 px-6" data-testid="pricing-section">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-accent mb-4 block">
            Pricing
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Choose the plan that fits your content creation needs.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`relative rounded-2xl overflow-hidden bg-surface border ${
                plan.highlighted 
                  ? 'border-brand-primary/30' 
                  : 'border-white/10'
              }`}
            >
              {/* Plan Header */}
              <div className="p-8 bg-surface-elevated">
                <span className="inline-block px-3 py-1 rounded text-xs font-bold uppercase tracking-wider mb-4 bg-brand-primary/20 text-brand-primary">
                  {plan.name}
                </span>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-5xl font-heading font-black text-white">{plan.price}</span>
                  <span className="text-gray-400">{plan.period}</span>
                </div>
                <p className="text-gray-400">{plan.description}</p>
              </div>

              {/* Features */}
              <div className="p-8">
                <ul className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-400" />
                      <span className="text-sm text-gray-200">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a
                  href={plan.ctaLink}
                  className={`flex items-center justify-center gap-2 w-full py-3.5 rounded-full font-semibold transition-all duration-300 mt-8 ${
                    plan.highlighted
                      ? 'bg-brand-primary hover:bg-brand-primary-hover text-surface-base hover:-translate-y-0.5 glow-primary'
                      : 'bg-brand-primary hover:bg-brand-primary-hover text-surface-base hover:-translate-y-0.5'
                  }`}
                  data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
